import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Drawer, Input, InputNumber, Popover, Radio } from 'antd';

import MeasurementValueInput from '../common/MeasurementValueInput';
import CurrencyValueInput from '../common/CurrencyValueInput';
import { booleanLabels, getNumericInputMode } from '../../utils/elements';
import { parseNumberEntry } from '../../utils/numberEntry';
import { ratingScale } from '../../utils/rating';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';
import { fieldHoldsChoiceList } from '../../utils/kqlBuilder';
import { TEXT_OPS, TEXT_OP_LABELS, draftConditions, draftProblem } from '../../utils/headerFilterModel';
import { DateChoice, PickedValues } from './HeaderFilterPickers';

/** What a choice checklist calls "no value". */
export const EMPTY_CHOICE_LABEL = '(Empty)';

const PRESENCE_WORDS = {
	file: { nempty: 'Has a file', empty: 'Has none' },
	entries: { nempty: 'Has entries', empty: 'Has none' },
};

/** A field's choices as `{ value, label }`, plus any value the filter holds that the list does not. */
function choiceOptions(field, held = []) {
	const options = (Array.isArray(field?.options?.choices) ? field.options.choices : [])
		.filter(choice => choice !== null && choice !== undefined && choice !== '')
		.map(choice => (typeof choice === 'object'
			? { value: String(choice.value), label: String(choice.label ?? choice.value) }
			: { value: String(choice), label: String(choice) }));
	const known = new Set(options.map(option => option.value));
	// A value the filter names that the list no longer holds stays in view,
	// ticked, so applying never drops it unseen.
	held.filter(value => !known.has(value)).forEach(value => options.push({ value, label: `${value} (not in the list)` }));
	return options;
}

function ChoiceList({ field, label, draft, onChange }) {
	const [search, setSearch] = useState('');
	const options = useMemo(() => choiceOptions(field, draft.values), [field, draft.values]);
	const needle = search.trim().toLowerCase();
	const shown = needle ? options.filter(option => option.label.toLowerCase().includes(needle)) : options;
	const showEmpty = !needle || EMPTY_CHOICE_LABEL.toLowerCase().includes(needle);
	const ticked = new Set(draft.values);
	const toggle = (value, on) => {
		const values = on ? [...draft.values, value] : draft.values.filter(entry => entry !== value);
		// (Empty) and a value are never ticked together: "any of A or empty" is
		// not one condition, and an `or` would fold every chip into one.
		onChange({ values, empty: false });
	};
	return (
		<>
			<Input
				{...NO_TEXT_ASSIST}
				className="header-filter__search"
				allowClear
				placeholder="Search values"
				aria-label={`Search the values of ${label}`}
				value={search}
				onChange={event => setSearch(event.target.value)}
			/>
			<div className="header-filter__checklist" role="group" aria-label={`Values of ${label}`}>
				{showEmpty ? (
					<Checkbox
						className="header-filter__check header-filter__check--empty"
						checked={draft.empty}
						onChange={event => onChange({ values: [], empty: event.target.checked })}
					>
						{EMPTY_CHOICE_LABEL}
					</Checkbox>
				) : null}
				{shown.map(option => (
					<Checkbox
						key={option.value}
						className="header-filter__check"
						checked={ticked.has(option.value)}
						onChange={event => toggle(option.value, event.target.checked)}
					>
						{option.label}
					</Checkbox>
				))}
				{!shown.length && !showEmpty ? <p className="header-filter__none">No value matches.</p> : null}
			</div>
			<p className="header-filter__hint">
				{fieldHoldsChoiceList(field) ? 'Shows items that have any of the ticked values.' : 'Shows items with any of the ticked values.'}
			</p>
		</>
	);
}

/** One end of a range, in the element's own control. */
function RangeEnd({ field, which, label, value, disabled, onChange, onEnter }) {
	const words = which === 'min' ? 'Min' : 'Max';
	const name = `${label}: ${words.toLowerCase()}`;
	if (field.dataType === 'measurement') {
		return <MeasurementValueInput raw={value} options={field.options} name={field.path.join('.')} label={label} aria-label={name} disabled={disabled} onChange={onChange} className="header-filter__end-input" />;
	}
	if (field.dataType === 'currency') {
		return <CurrencyValueInput raw={value} options={field.options} name={field.path.join('.')} label={label} aria-label={name} disabled={disabled} onChange={onChange} className="header-filter__end-input" />;
	}
	const scale = field.dataType === 'rating' ? ratingScale(field.options) : null;
	return (
		<InputNumber
			className="header-filter__end-input"
			aria-label={name}
			placeholder={words}
			value={value ?? null}
			disabled={disabled}
			// A typed fraction ("1 1/2", "¾") is the number it is, as in the builder.
			parser={text => parseNumberEntry(text)}
			inputMode={getNumericInputMode(field.dataType)}
			onChange={next => onChange(next === null ? undefined : next)}
			onPressEnter={onEnter}
			{...(scale ? { min: 0, max: scale.max, step: scale.allowHalf ? 0.5 : 1 } : {})}
		/>
	);
}

/**
 * The control for one column's filter, in the shape of its element's type
 * (utils/headerFilterModel.js names the kinds), with Clear and Apply. It holds
 * a draft; nothing reaches the address until Apply (or Enter in a box).
 */
export function HeaderFilterPanel({ column, label, read, onApply, onClear, onClose }) {
	const [draft, setDraft] = useState(read.draft);
	// Focus goes into the panel when it opens (a popover is portalled to the
	// end of the page, where Tab from the header would never reach it).
	const rootRef = useRef(null);
	useEffect(() => {
		const timer = setTimeout(() => {
			const root = rootRef.current;
			if (!root) return;
			// A box to type in first (the text, the search, the min); else the first choice.
			const target = root.querySelector('input.ant-input:not([disabled]), input.ant-input-number-input:not([disabled])')
				|| root.querySelector('input:not([disabled]), button:not([disabled])');
			target?.focus({ preventScroll: true });
		}, 0);
		return () => clearTimeout(timer);
	}, []);
	const patch = next => setDraft(current => ({ ...current, ...next }));
	// Given the query it goes into, so a filter grown past what a query holds says so.
	const problem = draftProblem(column, draft, { query: read.query });
	const field = column.field;
	const apply = () => { if (!problem) onApply(draft); };
	const canClear = read.active || draftConditions(column, draft).length > 0;

	let body = null;
	if (column.kind === 'text') {
		const typed = draft.op === 'contains' || draft.op === 'eq';
		body = (
			<>
				<Radio.Group
					className="header-filter__options"
					value={draft.op}
					aria-label={`How to match ${label}`}
					onChange={event => patch({ op: event.target.value })}
					options={TEXT_OPS.map(op => ({ value: op, label: TEXT_OP_LABELS[op] }))}
				/>
				{typed ? (
					<Input
						{...NO_TEXT_ASSIST}
						className="header-filter__text"
						placeholder="Text"
						aria-label={`${label} ${TEXT_OP_LABELS[draft.op].toLowerCase()}`}
						value={draft.text}
						onChange={event => patch({ text: event.target.value })}
						onPressEnter={apply}
					/>
				) : null}
			</>
		);
	} else if (column.kind === 'choice') {
		body = <ChoiceList field={field} label={label} draft={draft} onChange={setDraft} />;
	} else if (column.kind === 'boolean') {
		const { trueLabel, falseLabel } = booleanLabels(field);
		body = (
			<Radio.Group
				className="header-filter__options"
				value={draft.value}
				aria-label={label}
				onChange={event => setDraft({ value: event.target.value })}
				options={[{ value: true, label: trueLabel }, { value: false, label: falseLabel }, { value: 'empty', label: 'Empty' }]}
			/>
		);
	} else if (column.kind === 'range') {
		body = (
			<>
				<div className="header-filter__range">
					{['min', 'max'].map(which => (
						<label className="header-filter__end" key={which}>
							<span className="header-filter__end-label">{which === 'min' ? 'Min' : 'Max'}</span>
							<RangeEnd field={field} which={which} label={label} value={draft[which]} disabled={draft.empty} onChange={value => patch({ [which]: value })} onEnter={apply} />
						</label>
					))}
				</div>
				<Checkbox className="header-filter__check" checked={draft.empty} onChange={event => patch({ empty: event.target.checked })}>
					Is empty
				</Checkbox>
			</>
		);
	} else if (column.kind === 'date') {
		body = <DateChoice column={column} label={label} draft={draft} onChange={setDraft} />;
	} else if (column.kind === 'user' || column.kind === 'tag' || column.kind === 'lookup') {
		body = <PickedValues column={column} label={label} draft={draft} onChange={setDraft} />;
	} else if (column.kind === 'file' || column.kind === 'entries') {
		const words = PRESENCE_WORDS[column.kind];
		body = (
			<Radio.Group
				className="header-filter__options"
				value={draft.value}
				aria-label={label}
				onChange={event => setDraft({ value: event.target.value })}
				options={[{ value: 'nempty', label: words.nempty }, { value: 'empty', label: words.empty }]}
			/>
		);
	}

	return (
		<div
			ref={rootRef}
			className={`header-filter header-filter--${column.kind}`}
			role="group"
			aria-label={`Filter ${label}`}
			onKeyDown={(event) => {
				if (event.key === 'Escape') { event.stopPropagation(); onClose(); }
			}}
		>
			{body}
			{problem ? <p className="header-filter__problem" role="alert">{problem}</p> : null}
			<div className="header-filter__actions">
				<Button onClick={onClear} disabled={!canClear}>Clear</Button>
				<Button type="primary" onClick={apply} disabled={Boolean(problem)}>Apply</Button>
			</div>
		</div>
	);
}

/**
 * A column header's filter (KPR-1639, KPE-051 ruling i06): the panel in a
 * Popover under the header's funnel, or a bottom Drawer on a phone. `open` is
 * the header's; the panel is mounted afresh on each open (`session`, its
 * key), so it starts from the condition in the address.
 */
export default function HeaderFilter({ open, session = 0, onOpenChange, onDone, isMobile, column, label, read, onApply, onClear, children }) {
	// Apply, Clear and Escape hand focus back to the header (`onDone`); a click
	// elsewhere closes the panel and leaves focus where it went.
	const close = () => { onOpenChange(false); if (typeof onDone === 'function') onDone(); };
	const panel = open ? (
		<HeaderFilterPanel
			key={session}
			column={column}
			label={label}
			read={read}
			onClose={close}
			onApply={(draft) => { close(); onApply(draft); }}
			onClear={() => { close(); onClear(); }}
		/>
	) : null;
	if (isMobile) {
		return (
			<>
				{children}
				<Drawer open={open} placement="bottom" height="auto" className="header-filter__drawer" title={`Filter ${label}`} onClose={() => onOpenChange(false)}>
					{panel}
				</Drawer>
			</>
		);
	}
	return (
		<Popover
			open={open}
			onOpenChange={onOpenChange}
			trigger="click"
			placement="bottomLeft"
			autoAdjustOverflow={{ adjustX: true, adjustY: true, shiftX: true }}
			overlayClassName="header-filter__popover"
			title={`Filter ${label}`}
			content={panel}
		>
			{children}
		</Popover>
	);
}
